/**
 * «HISTÓRICO DE CHECK-INS» (Enrique, 28-10): la misma pantalla que el «Histórico de informes»
 * de Seguimiento, con todos sus check-in diarios, también los guardados a medias.
 *
 * Una fila por día: el día en su cuadro, el día de la semana y la fecha, y «Ver check-in ›».
 * El detalle se abre debajo de su fila (`detalleDelDia`, el mismo de «Tus días»). Los
 * guardados a medias (`GET /checkins/borradores`) llevan «Guardado parcial».
 */
import React, { useEffect, useState } from 'react';
import { Calendar, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { detalleDelDia, diaDelCierre, renglonesDelCierre } from '../lib/detalleDelCierre';

const ORANGE = '#FF671F';
const DE_UNA_VEZ = 30;

/**
 * Los enviados y los a medias, uno por día y de más nuevo a más viejo. Si un día tiene los
 * dos (el borrador llegó antes de que se borrara), manda el enviado.
 */
export const diasDelHistorico = (enviados, parciales) => {
    const porDia = new Map();
    for (const c of enviados || []) {
        const dia = diaDelCierre(c);
        if (dia && !porDia.has(dia)) porDia.set(dia, { dia, cierre: c, parcial: false });
    }
    for (const b of parciales || []) {
        if (b?.dia && !porDia.has(b.dia)) porDia.set(b.dia, { dia: b.dia, cierre: b, parcial: true });
    }
    return [...porDia.values()].sort((a, b) => (a.dia < b.dia ? 1 : a.dia > b.dia ? -1 : 0));
};

const Detalle = ({ cierre }) => {
    const filas = detalleDelDia(cierre);
    const renglones = renglonesDelCierre(cierre);
    if (!filas.length && !renglones.length) {
        return <p className="text-sm text-muted-foreground py-2">Sin respuestas guardadas.</p>;
    }
    return (
        <div data-testid="historico-checkin-detalle">
            <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1.5">
                {filas.map(f => (
                    <React.Fragment key={f.nombre}>
                        <dt className="text-[13px] text-foreground/50">{f.nombre}</dt>
                        <dd className={`text-[13px] break-words ${f.apagado ? 'text-foreground/40 italic' : 'text-foreground/85'}`}>{f.valor}</dd>
                    </React.Fragment>
                ))}
            </dl>
            {renglones.map(r => (
                <p key={r.titulo}
                    className="text-sm text-foreground/60 mt-2 whitespace-pre-line break-words border-l-2 border-border pl-3">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-foreground/40 mr-2">{r.titulo}</span>
                    {r.texto}
                </p>
            ))}
            {cierre.trainer_feedback && (
                <div className="mt-2 p-2 bg-brand/10 border border-brand/20 rounded-lg text-sm text-foreground/80 break-words">
                    <span className="text-[10px] uppercase tracking-wider text-brand font-bold mr-2">Entrenador:</span>
                    {cierre.trainer_feedback}
                </div>
            )}
        </div>
    );
};

/** @param abrirDia 'AAAA-MM-DD' que sale ya abierto: el que acaba de enviar (28-10). */
const HistoricoDeCheckIns = ({ abrirDia = null }) => {
    const { api } = useAuth();
    const [enviados, setEnviados] = useState([]);
    const [parciales, setParciales] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [hayMas, setHayMas] = useState(false);
    const [trayendo, setTrayendo] = useState(false);
    const [abierto, setAbierto] = useState(abrirDia);

    useEffect(() => {
        let vivo = true;
        Promise.all([
            api.get(`/checkins?type=daily&limit=${DE_UNA_VEZ}`).then(r => r.data).catch(err => {
                console.error('No se pudieron traer los check-in:', err?.response?.data || err);
                return [];
            }),
            api.get('/checkins/borradores').then(r => r.data).catch(err => {
                console.error('No se pudieron traer los check-in a medias:', err?.response?.data || err);
                return [];
            }),
        ]).then(([lista, medias]) => {
            if (!vivo) return;
            const l = Array.isArray(lista) ? lista : [];
            setEnviados(l);
            setHayMas(l.length === DE_UNA_VEZ);
            setParciales(Array.isArray(medias) ? medias : []);
            setCargando(false);
        });
        return () => { vivo = false; };
    }, [api]);

    const traerMas = async () => {
        setTrayendo(true);
        try {
            const { data } = await api.get(`/checkins?type=daily&limit=${DE_UNA_VEZ}&skip=${enviados.length}`);
            const mas = Array.isArray(data) ? data : [];
            setEnviados(prev => [...prev, ...mas]);
            setHayMas(mas.length === DE_UNA_VEZ);
        } catch (err) {
            console.error('No se pudieron traer más check-in:', err?.response?.data || err);
        } finally {
            setTrayendo(false);
        }
    };

    const dias = diasDelHistorico(enviados, parciales);

    return (
        <div className="space-y-3" data-testid="historico-checkins">
            <p className="text-lg font-bold text-foreground" data-testid="titulo-historico-checkins">Histórico de check-ins</p>
            {cargando ? (
                <div className="h-40 bg-card rounded-2xl animate-pulse" />
            ) : dias.length > 0 ? (
                <>
                    <p className="text-[11px] font-bold uppercase tracking-wider text-foreground/50 px-1">
                        Registros
                    </p>
                    <div className="bg-card border border-border rounded-2xl overflow-hidden divide-y divide-border">
                        {dias.map(({ dia, cierre, parcial }) => {
                            const f = new Date(`${dia}T12:00:00`);
                            const mesCorto = f.toLocaleDateString('es-ES', { month: 'short' }).replace('.', '');
                            const diaSemana = f.toLocaleDateString('es-ES', { weekday: 'long' });
                            const esteAbierto = abierto === dia;
                            const enlace = esteAbierto ? 'Ocultar check-in' : 'Ver check-in';
                            return (
                                <div key={dia}>
                                    <button type="button" onClick={() => setAbierto(a => (a === dia ? null : dia))}
                                        data-testid={`historico-checkin-${dia}`}
                                        className="w-full text-left flex items-center gap-3 sm:gap-4 px-3 sm:px-5 py-3 sm:py-4 hover:bg-muted/40 transition-colors">
                                        <div className="w-12 h-12 sm:w-14 sm:h-14 flex-shrink-0 rounded-xl bg-muted flex flex-col items-center justify-center leading-none">
                                            <span className="text-xl sm:text-2xl font-bold text-foreground" style={{ fontFamily: 'Barlow Condensed' }}>
                                                {f.getDate()}
                                            </span>
                                            <span className="text-[9px] sm:text-[10px] font-bold uppercase tracking-wider text-foreground/50 mt-1">
                                                {mesCorto}
                                            </span>
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-[15px] font-semibold text-foreground">
                                                <span className="capitalize">{diaSemana}</span>
                                                {parcial && (
                                                    <span className="ml-2 align-middle inline-block text-[10px] font-bold uppercase tracking-wider text-amber-300 border border-amber-300/40 rounded-full px-2 py-0.5"
                                                        data-testid={`historico-checkin-parcial-${dia}`}>
                                                        Guardado parcial
                                                    </span>
                                                )}
                                            </p>
                                            <p className="text-[13px] text-foreground/50 truncate">
                                                {f.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
                                            </p>
                                            {/* En el móvil el enlace va aquí debajo: a la derecha no cabe. */}
                                            <p className="sm:hidden text-[13px] font-semibold mt-0.5" style={{ color: ORANGE }}>
                                                {enlace}
                                            </p>
                                        </div>
                                        <span className="flex items-center gap-1.5 flex-shrink-0 text-sm font-semibold" style={{ color: ORANGE }}>
                                            <span className="hidden sm:inline">{enlace}</span>
                                            <ChevronRight className={`w-4 h-4 transition-transform ${esteAbierto ? 'rotate-90' : ''}`} />
                                        </span>
                                    </button>
                                    {esteAbierto && (
                                        <div className="px-3 sm:px-5 pb-4">
                                            <Detalle cierre={cierre} />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </>
            ) : (
                <div className="bg-card border border-border rounded-2xl p-8 text-center">
                    <Calendar className="w-10 h-10 text-foreground/20 mx-auto mb-3" />
                    <p className="text-foreground/40 text-sm">No hay check-ins anteriores.</p>
                </div>
            )}
            {hayMas && (
                <button onClick={traerMas} disabled={trayendo} data-testid="historico-checkins-mas"
                    className="w-full py-3 rounded-2xl border border-border text-sm text-foreground/60 hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50">
                    {trayendo ? 'Cargando...' : 'Cargar más check-ins'}
                </button>
            )}
        </div>
    );
};

export default HistoricoDeCheckIns;
